import { CalendarMonth, Close, Search, MedicalServices } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import athenaService from "../services/athenaService";
import AppointmentTypeMapper from "../services/appointmentTypeMapper";
import useStore, { useStoreActions } from "../store.js";
import AppointmentConfirm from "./reuse/appointment/AppointmentConfirm";
import AppointmentInsurance from "./reuse/appointment/AppointmentInsurance";
import AppointmentBooking from "./reuse/appointment/AppointmentResult";
import AppointmentUserForm from "./reuse/appointment/AppointmentUserForm";

const AppointmentScheduler = () => {
  const [screen, setScreen] = useState(1);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // New booking mode and patient type states
  const [bookingType, setBookingType] = useState('appointment'); // 'appointment' or 'iv'
  const [patientType, setPatientType] = useState('new'); // 'new' or 'existing' - default to 'new'
  
  // Patient search and selection states
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState({ firstName: '', lastName: '', dob: null });
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // New states for appointment reasons
  const [appointmentReasons, setAppointmentReasons] = useState([]);
  const [selectedReason, setSelectedReason] = useState('');

  // zustand
  const { departments, providers, provider, departmentprovider, appointmentTypes, appointmentReason, selectedDepartment, selectedProvider, selectedType, isNewPatient } = useStore();
  
  // Add state for locations (grouped departments)
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  
  // Add snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [departmentsByLocation, setDepartmentsByLocation] = useState({});

  const { setDepartments, setProviders, setProvider, setDepartmentProvider, setAppointmentTypes, setAppointmentReason, setSelectedDepartment, setSelectedProvider, setSelectedType, setIsNewPatient, setPatient } = useStoreActions();

  // Add flag to track if search was performed manually
  const [hasSearched, setHasSearched] = useState(false);

  // Add provider search filter
  const [providerSearchFilter, setProviderSearchFilter] = useState('');

  // Helper function to convert string to camel case
  const toCamelCase = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Load departments on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);
        await loadDepartments();
        await loadPatients(); // Load patients data
        
        // Set initial patient type to 'new' to have a radio button selected by default
        // This should trigger appointment type filtering when a provider is selected
        setPatientType('new');
        setIsNewPatient(true);
      } catch (error) {
        console.error("Error loading initial data:", error);
        setError("Failed to load initial data: " + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    console.log(
      "All Departments:",
      departments.map((d) => ({
        id: d.departmentid,
        name: d.name,
      }))
    );
  }, [departments]);

  useEffect(() => {
    console.log(
      "All Providers:",
      providers.map((p) => ({
        id: p.providerid,
        name: p.providername || p.firstname + " " + p.lastname,
      }))
    );
  }, [providers]);

  useEffect(() => {
    console.log(
      "All Appointment Types:",
      appointmentTypes.map((t) => ({
        id: t.appointmenttypeid,
        name: t.name,
        duration: t.duration,
        patienttype: t.patienttype,
      }))
    );
  }, [appointmentTypes]);

  // Debug appointmentReasons changes
  useEffect(() => {
    console.log(
      "All Appointment Reasons:",
      appointmentReasons.map((r) => ({
        id: r.reasonid,
        reason: r.reason,
        description: r.description,
      }))
    );
  }, [appointmentReasons]);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      setError(null);
      const deps = await athenaService.getDepartments();
      console.log("Loaded departments:", deps);
      
      // Filter out departments with empty providerlist
      const filteredDepts = deps.filter(dept => 
        dept.providerlist && dept.providerlist.length > 0
      );
      console.log("AppointmentScheduler: Departments with providers:", filteredDepts);
      setDepartments(filteredDepts || []);
      
      // Group departments by location (city)
      const deptsByLocation = {};
      const locationList = [];
      
      filteredDepts.forEach(dept => {
        const locationName = toCamelCase(dept.city) || 'Unknown';
        
        // Initialize location group if it doesn't exist
        if (!deptsByLocation[locationName]) {
          deptsByLocation[locationName] = [];
          locationList.push({
            name: locationName,
            departments: []
          });
        }
        
        // Add department to its location group
        deptsByLocation[locationName].push(dept);
        
        // Update the location list with this department
        const locationIndex = locationList.findIndex(loc => loc.name === locationName);
        if (locationIndex >= 0) {
          locationList[locationIndex].departments.push(dept);
        }
      });
      
      console.log("Departments grouped by location:", deptsByLocation);
      console.log("Location list:", locationList);
      
      setLocations(locationList);
      setDepartmentsByLocation(deptsByLocation);
    } catch (err) {
      console.error("Error loading departments:", err);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load departments';
      
      if (err.response?.data?.error) {
        // Handle structured error from API
        const apiError = err.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (err.response?.data?.message) {
        // Handle general API error message
        errorMessage = err.response.data.message;
      } else if (err.message) {
        // Use the error's message property
        errorMessage = err.message;
      }
      
      setError(`Failed to load departments: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Modified to load providers for a single department
  const loadProvidersForDepartment = async (departmentId) => {
    console.log("Loading providers for department:", departmentId);
    if (!departmentId) return [];
    try {
      // Find the selected department to get its providerlist
      const department = departments.find(d => 
        d.departmentid === departmentId || 
        d.departmentid === parseInt(departmentId)
      );
      
      // Get all providers for the department
      const provs = await athenaService.getProviders(parseInt(departmentId));
      
      // Filter out non-person entities first
      let humanProviders = provs.filter(p => p.entitytype === 'Person');
      
      // Apply initial filtering based on booking type
      let filteredProvs = [...humanProviders];
      
      // For regular appointments, remove IV providers
      if (bookingType === 'appointment') {
        filteredProvs = filteredProvs.filter(p => 
          p.displayname !== 'IV_HOME' && p.displayname !== 'IV_OFFICE'
        );
      } 
      // For IV service, only include IV_OFFICE provider (but since we filtered non-person entities, handle this differently)
      else if (bookingType === 'iv') {
        // For IV service, we need to include the IV_OFFICE provider even though it's non-person
        const ivOfficeProvider = provs.find(p => p.displayname === 'IV_OFFICE');
        if (ivOfficeProvider) {
          filteredProvs = [ivOfficeProvider];
        } else {
          filteredProvs = [];
        }
      }
      
      // Format provider names consistently for human providers
      filteredProvs = filteredProvs.map(provider => {
        if (provider.entitytype === 'Person') {
          const firstName = provider.firstname || '';
          const lastName = provider.lastname || '';
          const degree = provider.providertypeid || '';
          
          // Create formatted display name: "FirstName LastName, DEGREE"
          let formattedName = '';
          if (firstName && lastName) {
            formattedName = `${firstName} ${lastName}`;
            if (degree) {
              formattedName += `, ${degree}`;
            }
          } else if (provider.displayname) {
            // Fallback to displayname if first/last not available
            formattedName = provider.displayname;
          }
          
          return {
            ...provider,
            formattedDisplayName: formattedName
          };
        }
        // For non-person entities (like IV_OFFICE), keep original displayname
        return {
          ...provider,
          formattedDisplayName: provider.displayname || provider.lastname || ''
        };
      });
      
      // If department has a providerlist, further filter providers to only include those in the list
      if (department && department.providerlist && department.providerlist.length > 0) {
        filteredProvs = filteredProvs.filter(p => 
          department.providerlist.includes(p.providerid.toString())
        );
      }
      
      return filteredProvs;
    } catch (err) {
      console.error(`Error loading providers for department ${departmentId}:`, err);
      
      // Extract detailed error message if available
      let errorMessage = `Could not load providers for department ${departmentId}`;
      
      if (err.response?.data?.error) {
        // Handle structured error from API
        const apiError = err.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (err.response?.data?.message) {
        // Handle general API error message
        errorMessage = err.response.data.message;
      } else if (err.message) {
        // Use the error's message property
        errorMessage = err.message;
      }
      
      // Show error in snackbar
      setSnackbar({
        open: true,
        message: `Failed to load providers: ${errorMessage}`,
        severity: 'error'
      });
      
      return [];
    }
  };
  
  // New function to load providers for all departments in a location
  const loadProvidersForLocation = async (locationName) => {
    console.log("Loading providers for location:", locationName);
    if (!locationName) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Get all departments for this location
      const locationDepartments = departmentsByLocation[locationName] || [];
      console.log(`Found ${locationDepartments.length} departments for location ${locationName}`);
      
      if (locationDepartments.length === 0) {
        setProviders([]);
        setDepartmentProvider([]);
        setLoading(false);
        return;
      }
      
      // Store the first department ID for appointment type loading later
      if (locationDepartments.length > 0) {
        setSelectedDepartment(locationDepartments[0].departmentid);
      }
      
      // Load providers for all departments in this location
      const allProviders = [];
      const allProviderIds = new Set();
      
      // Process each department to get its providers
      for (const dept of locationDepartments) {
        const deptProviders = await loadProvidersForDepartment(dept.departmentid);
        
        // Add only unique providers to the list
        deptProviders.forEach(provider => {
          if (!allProviderIds.has(provider.providerid)) {
            allProviderIds.add(provider.providerid);
            allProviders.push(provider);
          }
        });
      }
      
      console.log(`Found ${allProviders.length} unique providers for location ${locationName}`);
      
      // Special handling for IV service booking type
      if (bookingType === 'iv') {
        const ivOfficeProvider = allProviders.find(p => p.displayname === 'IV_OFFICE');
        if (ivOfficeProvider) {
          // Automatically select the IV_OFFICE provider
          setSelectedProvider(ivOfficeProvider.providerid);
          
          // Then load appointment types for this provider using the first department
          setTimeout(() => {
            loadAppointmentTypes(locationDepartments[0].departmentid, ivOfficeProvider.providerid);
          }, 100);
        }
      }
      
      // Set the combined list of providers
      setProviders(allProviders);
      
      // Collect all provider IDs for the dropdown
      const allProviderIdList = Array.from(allProviderIds).map(id => id.toString());
      setDepartmentProvider(allProviderIdList);
      
    } catch (err) {
      console.error("Error loading providers for location:", err);
      setError("Failed to load providers. Please try again.");
      setProviders([]);
      setDepartmentProvider([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Keep original function for backward compatibility and direct department selection
  const loadProviders = async (departmentId) => {
    console.log("Legacy loadProviders called for department:", departmentId);
    if (!departmentId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const providers = await loadProvidersForDepartment(departmentId);
      setProviders(providers || []);
      
      // Find the selected department to get its providerlist
      const department = departments.find(d => 
        d.departmentid === departmentId || 
        d.departmentid === parseInt(departmentId)
      );
      
      if (department && department.providerlist && department.providerlist.length > 0) {
        setDepartmentProvider(department.providerlist);
      } else {
        setDepartmentProvider([]);
      }
      
    } catch (err) {
      console.error("Error in legacy loadProviders:", err);
      setError("Failed to load providers. Please try again.");
      setProviders([]);
      setDepartmentProvider([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentProviders = (departmentId) => {
    const department = departments.find((d) => d.departmentid === departmentId);
    if (department) {
      setDepartmentProvider(department.providerlist);
      console.log('✅', providers.filter((p) => department.providerlist.includes(p.providerid.toString())));
    }
  };

  const loadAppointmentReasons = async (departmentId, providerId, patientTypeOverride) => {
    if (!departmentId || !providerId) return;
    
    // Use passed-in patient type if provided, otherwise use state
    const currentPatientType = patientTypeOverride || patientType;
    
    try {
      setLoading(true);
      setError(null);
      
      let reasons = [];
      let defaultReasons = [];
      
      console.log(`Loading appointment reasons for patient type: ${currentPatientType}`);
      
      if (currentPatientType === 'new') {
        // Load new patient reasons
        const response = await athenaService.getAppointmentReasonforNewPatient(departmentId, providerId);
        console.log("New patient reasons response:", response);
        
        if (response && response.patientappointmentreasons && response.patientappointmentreasons.length > 0) {
          // Filter to only show "New Patient" reasons
          reasons = response.patientappointmentreasons.filter(reason => 
            reason.reason === "New Patient"
          );
        }
        
        // Default reasons for new patients
        defaultReasons = [
          { reasonid: '11', reason: "New Patient", description: "I am a new patient and I would like to schedule my first visit." }
        ];
        
      } else if (currentPatientType === 'existing') {
        // Load existing patient reasons
        const response = await athenaService.getAppointmentReasonforExistingPatient(departmentId, providerId);
        console.log("Existing patient reasons response:", response);
        
        let followUpReasons = [];
        let telehealthReason = null;
        
        if (response && response.patientappointmentreasons && response.patientappointmentreasons.length > 0) {
          // Filter to get "Follow Up" reasons
          followUpReasons = response.patientappointmentreasons.filter(reason => 
            reason.reason === "Follow Up"
          );
          
          // Check if Telehealth is already in the API response
          telehealthReason = response.patientappointmentreasons.find(reason => 
            reason.reason === "Telehealth"
          );
        }
        
        // Always ensure TeleHealth is available for existing patients
        if (!telehealthReason) {
          telehealthReason = { 
            reasonid: 'default_telehealth', 
            reason: "Telehealth", 
            description: "I would like to schedule a telehealth appointment." 
          };
        }
        
        // Combine Follow Up reasons with Telehealth
        reasons = [...followUpReasons, telehealthReason];
        
        // Default reasons for existing patients (Follow Up + Telehealth) - used as fallback only
        defaultReasons = [
          { reasonid: '4', reason: "Follow Up", description: "I need to follow up with my provider about a condition or other issue I have." },
          { reasonid: 'default_telehealth', reason: "Telehealth", description: "I would like to schedule a telehealth appointment." }
        ];
      }
      
      // Use API reasons if available (for existing patients, we already combined them), otherwise use defaults
      const finalReasons = reasons.length > 0 ? reasons : defaultReasons;
      
      console.log(`Setting appointment reasons for ${currentPatientType} patient:`, finalReasons);
      setAppointmentReasons(finalReasons);
      
      // Clear selected reason when reasons change
      setSelectedReason('');
      
    } catch (err) {
      console.error("Error loading appointment reasons:", err);
      
      // Provide fallback defaults based on patient type
      const fallbackReasons = currentPatientType === 'new' 
        ? [{ reasonid: '11', reason: "New Patient", description: "I am a new patient and I would like to schedule my first visit." }]
        : [
            { reasonid: '4', reason: "Follow Up", description: "I need to follow up with my provider about a condition or other issue I have." },
            { reasonid: 'default_telehealth', reason: "Telehealth", description: "I would like to schedule a telehealth appointment." }
          ];
      
      setAppointmentReasons(fallbackReasons);
      setSelectedReason('');
      setError("Failed to load appointment reasons. Using defaults.");
    } finally {
      setLoading(false);
    }
  };

  const loadAppointmentTypes = async (departmentId, providerId, patientTypeOverride) => {
    // Use passed-in patient type if provided, otherwise use state
    const currentPatientType = patientTypeOverride || patientType;
    
    console.log(`🔧 loadAppointmentTypes called with:`, {
      departmentId,
      providerId,
      patientTypeOverride,
      currentPatientType,
      statePatientType: patientType
    });
    
    if (!departmentId || !providerId) return;
    try {
      setLoading(true);
      setError(null);
      const types = await athenaService.getAppointmentTypes(departmentId, providerId);
      console.log("Loaded appointment types:", types);
      
      // Extract types array
      let typesArray = [];
      if (Array.isArray(types)) {
        typesArray = types;
      } else {
        // This handles the case where the API returns an object with appointmenttypes property
        typesArray = types?.appointmenttypes || [];
      }
      
      // Filter appointment types based on booking type and patient type
    let filteredTypes = [...typesArray];
    
    if (bookingType === 'iv') {
      // For IV booking, ignore patient type and filter to only show INJECTION/IV types
      console.log('Filtering for IV service types only (ignoring patient type)');
        const ivKeywords = ['INJECTION', 'IV', 'INFUSION'];
        
        filteredTypes = filteredTypes.filter(type => {
          const typeName = type.name.toUpperCase();
          return ivKeywords.some(keyword => typeName.includes(keyword));
        });
        
        // If no IV types found from API, use default IV appointment type
        if (filteredTypes.length === 0) {
          const defaultIvType = {
            "appointmenttypeid": 9,
            "group": false,
            "templatetypeonly": false,
            "name": "INJECTION/ IV",
            "shortname": "INJ",
            "duration": 30,
            "patientdisplayname": "INJECTION/IV",
            "patient": true,
            "generic": false,
            "createencounteroncheckin": false
          };
          filteredTypes = [defaultIvType];
          console.log('No IV types found from API, using default IV appointment type');
        }
        
        console.log('IV service filtered types:', filteredTypes);
        
      // Auto-select INJECTION/IV type for IV service
      if (filteredTypes.length > 0) {
        // Convert appointment types to reason format for the dropdown
        const appointmentTypeReasons = filteredTypes.map(type => ({
          reasonid: type.appointmenttypeid,
          reason: type.name,
          description: `${type.name} appointment (${type.duration} minutes)`
        }));
        
        // Set the appointment types as reasons for IV service
        setAppointmentReasons(appointmentTypeReasons);
        console.log('Set appointment reasons for IV service:', appointmentTypeReasons);
        
        // Prefer INJECTION/IV specifically if available
        const exactIvType = filteredTypes.find(type => 
          type.name.toUpperCase().includes('INJECTION/IV') || 
          type.name.toUpperCase().includes('INJECTION / IV')
        );
        
        let selectedTypeId;
        if (exactIvType) {
          selectedTypeId = exactIvType.appointmenttypeid;
          setSelectedReason(selectedTypeId);
          setSelectedType(selectedTypeId);
          console.log('Auto-selected INJECTION/IV appointment type:', exactIvType.name);
        } else {
          // Otherwise just use the first filtered type
          selectedTypeId = filteredTypes[0].appointmenttypeid;
          setSelectedReason(selectedTypeId);
          setSelectedType(selectedTypeId);
          console.log('Auto-selected first IV appointment type:', filteredTypes[0].name);
        }
        
        // Auto-search for IV slots after a short delay to ensure state updates
        // setTimeout(() => {
        //   if (departmentId && providerId) {
        //     console.log('Auto-searching for IV appointment slots...');
        //     handleFindAvailableTimes();
        //   }
        // }, 500);
      }
    } else if (bookingType === 'appointment') {
      // For regular appointments, filter based on patient type
      console.log(`Filtering appointment types - patient type: ${currentPatientType}`);
      
      if (currentPatientType === 'new') {
          // For new patients, strictly only show NEW PATIENT appointment types
          console.log('Filtering for NEW PATIENT types only');
          filteredTypes = filteredTypes.filter(type => {
            const typeName = type.name.toUpperCase();
            return typeName.includes('NEW PATIENT');
          });
          console.log('Filtered new patient types:', filteredTypes.map(t => t.name));
          
        } else if (currentPatientType === 'existing') {
          // For existing patients, only show FOLLOW UP and TELEHEALTH
          filteredTypes = filteredTypes.filter(type => 
            type.name.toUpperCase().includes('FOLLOW UP') || 
            type.name.toUpperCase().includes('TELEHEALTH')          );
        }
      }
      
      console.log("Setting filtered appointment types:", filteredTypes);
      setAppointmentTypes(filteredTypes);
    } catch (err) {
      console.error("Error loading appointment types:", err);
      setError("Failed to load appointment types. Please try again.");
      setAppointmentTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      // Get the first department and provider if none are selected yet
      // This is to handle the initial load where no selections have been made
      const defaultDepartmentId = departments.length > 0 ? departments[0].departmentid : null;
      let departmentId = selectedDepartment || defaultDepartmentId;
      
      // If we have a department but no provider, try to get the first provider for that department
      let providerId = selectedProvider;
      if (departmentId && !providerId && providers.length > 0) {
        providerId = providers[0].providerid;
      }
      
      // Only proceed if we have valid IDs
      if (departmentId) {
        console.log(`Getting patients with departmentId: ${departmentId}, providerId: ${providerId || 'not specified'}`);
        
        // Try to get patients from appointments first with department and provider IDs
        const appointmentPatients = await athenaService.getPatientsFromAppointments({
          departmentId: departmentId,
          providerId: providerId
        });
        
        if (appointmentPatients && appointmentPatients.length > 0) {
          console.log(`Using ${appointmentPatients.length} patients from appointments`);
          setPatients(appointmentPatients);
          return;
        }
      }
      
      // Fallback to localStorage patients
      console.log('No appointment patients found or missing department/provider IDs, using localStorage');
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      setPatients(localPatients);
    } catch (error) {
      console.error("Error loading patients:", error);
      // Fallback to localStorage if API fails
      const localPatients = JSON.parse(localStorage.getItem('patients') || '[]');
      setPatients(localPatients);
    }
  };

  // New function to handle location selection
  const handleLocationChange = async (event) => {
    const location = event.target.value;
    console.log("Selected location:", location);
    
    setSelectedLocation(location);
    setSelectedProvider("");
    setSelectedType("");
    setProviders([]);
    setAppointmentTypes([]);
    setAvailableSlots([]); // Reset available slots when location changes
    setHasSearched(false); // Reset search flag when location changes
    setProviderSearchFilter(''); // Reset provider search filter
    
    if (location) {
      await loadProvidersForLocation(location);
    }
  };
  
  // Keep the original function for backward compatibility
  const handleDepartmentChange = async (event) => {
    const deptId = event.target.value;
    console.log(
      "Legacy handleDepartmentChange called for department:",
      departments.find((d) => d.departmentid === deptId)
    );
    loadDepartmentProviders(deptId);
    setSelectedDepartment(deptId);
    setSelectedProvider("");
    setSelectedType("");
    setProviders([]);
    setAppointmentTypes([]);
    if (deptId) {
      await loadProviders(deptId);
    }
  };

  // Function to handle patient search
  const handlePatientSearchChange = (e) => {
    const { name, value } = e.target;
    setPatientSearch(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to search for patients
  const handleSearchPatients = () => {
    if (!patientSearch.firstName && !patientSearch.lastName) {
      // Show all patients if no search criteria
      setPatientSearchResults(patients);
      return;
    }

    // Filter patients based on search criteria
    const results = patients.filter(patient => {
      const matchFirstName = patientSearch.firstName && 
        patient.firstname && 
        patient.firstname.toLowerCase().includes(patientSearch.firstName.toLowerCase());
      
      const matchLastName = patientSearch.lastName && 
        patient.lastname && 
        patient.lastname.toLowerCase().includes(patientSearch.lastName.toLowerCase());
      
      // Return patients that match any of the criteria
      return (patientSearch.firstName ? matchFirstName : true) && 
             (patientSearch.lastName ? matchLastName : true);
    });
    
    setPatientSearchResults(results);
  };

  // Function to select a patient
  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    // Store the patient ID in Zustand store for later use
    if (patient && patient.patientid) {
      setPatient(parseInt(patient.patientid));
    }
  };

  const handleSelectPatient = (patient) => {
    selectPatient(patient);
  };

  const handleProviderChange = async (event) => {
    const provId = event.target.value;
    console.log(
      "Selected provider details:",
      providers.find((p) => p.providerid === provId)
    );
    // Reset all relevant state when provider changes
    setAvailableSlots([]);
    setProvider(null); // Clear the current provider to prevent showing old provider data
    setSelectedProvider(provId);
    setSelectedType("");
    setSelectedReason("");
    setAppointmentReasons([]);
    setAvailableSlots([]); // Reset available slots when provider changes
    setHasSearched(false); // Reset search flag when provider changes
    setProviderSearchFilter(''); // Reset provider search filter
    
    if (provId && selectedDepartment) {
      await loadAppointmentTypes(selectedDepartment, provId);
      
      // For regular appointments, load appointment reasons
      if (bookingType === 'appointment') {
      await loadAppointmentReasons(selectedDepartment, provId);
      }
      // For IV services, appointment reasons are set in loadAppointmentTypes
    }
  };

  const handleTypeChange = (event) => {
    const typeId = event.target.value;
    console.log(
      "Selected appointment type details:",
      appointmentTypes.find((t) => t.appointmenttypeid === typeId)
    );
    setAvailableSlots([]); // Reset available slots when appointment type changes
    
    // Also reset provider display to prevent showing stale provider data
    if (provider) {
      // Store just the ID to prevent showing old provider details
      const currentProviderId = selectedProvider;
      setProvider(null);
      // We'll let the search function set the full provider data when needed
    }
    
    setSelectedType(typeId);
  };

  // New handler for reason changes
  const handleReasonChange = (event) => {
    const reasonId = event.target.value;
    const selectedReasonObj = appointmentReasons.find(r => r.reasonid === reasonId);
    
    console.log("Selected reason details:", selectedReasonObj);
    
    // Reset available slots when reason changes
    setAvailableSlots([]);
    
    // Clear provider display to prevent showing stale data
    if (provider) {
      setProvider(null);
    }
    
    setHasSearched(false); // Reset search flag when reason changes
    setSelectedReason(reasonId);
  };

  const handleFindAvailableTimes = async () => {
    try {
      setLoading(true);
      setError(null);
      setHasSearched(true); // Mark that a manual search was performed

      // Check if we have a valid reason selected
      if (!selectedReason) {
        setError("Please select a reason for visit");
        setAvailableSlots([]);
        return;
      }

      // For regular appointments, check Telehealth restrictions
      if (bookingType === 'appointment') {
        const selectedReasonObj = appointmentReasons.find(r => r.reasonid === selectedReason);
        
        console.log("Debug - selectedReason:", selectedReason, "type:", typeof selectedReason);
        console.log("Debug - selectedReasonObj:", selectedReasonObj);
        
        // Removed Telehealth restriction - all reasons should be allowed to search for slots
      }

      // Get today's date and 30 days from now
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 90);

      // Format dates for API
      const formattedStartDate = athenaService.formatDate(startDate);
      const formattedEndDate = athenaService.formatDate(endDate);

      // Get the full objects for logging
      const department = departments.find((d) => d.departmentid === selectedDepartment);
      const providerObj = providers.find((p) => p.providerid === selectedProvider);

      // Create a fresh provider object to ensure React detects changes
      const currentProvider = {...providerObj};
      currentProvider._timestamp = Date.now();
      setProvider(currentProvider);

      if (bookingType === 'iv') {
        // For IV service, use the old method with appointment type from selectedReason
        const appointmentType = appointmentTypes.find((t) => t.appointmenttypeid === selectedReason);
        
        if (!appointmentType) {
          setError("No IV appointment type selected. Please try again.");
          setAvailableSlots([]);
          return;
        }
        
        console.log("Finding available times for IV service with:", {
        department: {
          id: department?.departmentid,
          name: department?.name,
        },
        provider: {
            id: providerObj?.providerid,
            name: providerObj?.providername || `${providerObj?.firstname} ${providerObj?.lastname}`,
        },
        appointmentType: {
          id: appointmentType?.appointmenttypeid,
          name: appointmentType?.name,
        },
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
      
        // Use old method for IV services
        const slots = await athenaService.getAvailableSlots(
          selectedDepartment, 
          appointmentType, 
          selectedProvider, 
          formattedStartDate, 
          formattedEndDate
        );

        if (slots.length === 0) {
          setError("No available IV appointment slots found. Try a different date range.");
          setAvailableSlots([]);
          return;
        }

        // Apply collision detection for IV services
        let finalSlots = slots;
        if (appointmentType && appointmentType.duration) {
          console.log(`Applying collision detection with duration: ${appointmentType.duration} minutes`);
          finalSlots = filterSlotsByDuration(slots, appointmentType.duration);
        }
        
        console.log(`Found ${slots.length} IV slots, final ${finalSlots.length} slots after collision detection`);
        setAvailableSlots(finalSlots);
        
      } else {
        // For regular appointments, use reason-based method
        const selectedReasonObj = appointmentReasons.find(r => r.reasonid === selectedReason);
        
        console.log("Finding available times with reason:", {
          department: {
            id: department?.departmentid,
            name: department?.name,
          },
          provider: {
            id: providerObj?.providerid,
            name: providerObj?.providername || `${providerObj?.firstname} ${providerObj?.lastname}`,
          },
          reason: selectedReasonObj,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });

        // Use reason-based slot finding for regular appointments
        const slots = await athenaService.getOpenAppointmentSlotsByReason(
          selectedDepartment, 
          selectedReason, 
          formattedStartDate, 
          selectedProvider, 
          formattedEndDate
        );

      if (slots.length === 0) {
        setError("No available appointment slots found for the selected criteria. Try different options or a different date range.");
        setAvailableSlots([]);
          return;
        }

        // Apply prioritization based on patient type for regular appointments
        const prioritizedSlots = prioritizeSlotsByPatientType(slots);
        
        // Apply collision detection if slots have duration information
        let finalSlots = prioritizedSlots;
        if (prioritizedSlots.length > 0 && prioritizedSlots[0].duration) {
          const duration = prioritizedSlots[0].duration; // Use duration from the first slot
          console.log(`Applying collision detection with duration: ${duration} minutes`);
          // finalSlots = filterSlotsByDuration(prioritizedSlots, duration);
          finalSlots = prioritizedSlots;
      } else {
          console.log("No duration information found in slots, skipping collision detection");
      }
      
        console.log(`Found ${slots.length} slots, prioritized to ${prioritizedSlots.length} slots, final ${finalSlots.length} slots after collision detection`);
        setAvailableSlots(finalSlots);
      }
      
    } catch (error) {
      setAvailableSlots([]);
      console.error("Error finding available times:", error);
      
      // Extract detailed error message if available
      let errorMessage = 'Could not load available appointment times';
      
      if (error.response?.data?.error) {
        // Handle structured error from API
        const apiError = error.response.data.error;
        errorMessage = apiError.detailedmessage || apiError.error || apiError.message || errorMessage;
      } else if (error.response?.data?.message) {
        // Handle general API error message
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Use the error's message property
        errorMessage = error.message;
      }
      
      // Display detailed error message
      setError(`Failed to find available times: ${errorMessage}`);
      
      // Also show in snackbar for immediate visibility
      setSnackbar({
        open: true,
        message: `Could not load available slots: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to prioritize slots based on patient type
  const prioritizeSlotsByPatientType = (slots) => {
    if (!slots || slots.length === 0) return [];
    
    console.log(`Starting slot prioritization for ${patientType} patient with ${slots.length} slots`);
    
    // Debug: Log available appointment types and sample slot info
    console.log("🔍 Available appointment types for mapping:", appointmentTypes?.map(t => ({
      name: t.name,
      id: t.appointmenttypeid,
      duration: t.duration
    })));
    
    console.log("🔍 Sample slot before mapping:", slots[0] ? {
      type: slots[0].patientappointmenttypename,
      typeId: slots[0].appointmenttypeid,
      duration: slots[0].duration
    } : 'No slots available');
    
    // First, map slots to appropriate appointment types based on duration and patient type
    const mappedSlots = AppointmentTypeMapper.mapSlotsToAppointmentTypes(
      slots, 
      patientType, 
      appointmentTypes
    );
    
    console.log(`Mapped ${slots.length} slots to appropriate appointment types`);
    
    console.log("🔍 Sample slot after mapping:", mappedSlots[0] ? {
      type: mappedSlots[0].patientappointmenttypename,
      typeId: mappedSlots[0].appointmenttypeid,
      duration: mappedSlots[0].duration
    } : 'No mapped slots available');
    
    // Get all unique appointment type names present in the mapped slots
    const presentTypeNames = [...new Set(mappedSlots.map(slot => slot.patientappointmenttypename))];
    console.log("Present appointment type names in mapped slots:", presentTypeNames);
    
    let filteredSlots = [];
    
    if (patientType === 'existing') {
      // For existing patients: Follow-up types + ANY types
      const followUpTypes = presentTypeNames.filter(name => 
        name && (name.toLowerCase().includes('follow') || name.toLowerCase().includes('any') || name.toLowerCase().includes('telehealth'))
      );
      
      console.log("Found Follow-Up type names:", followUpTypes);
      
      // Filter to only include Follow-Up type slots
      filteredSlots = mappedSlots.filter(slot => 
        followUpTypes.includes(slot.patientappointmenttypename)
      );
      
      console.log(`Filtered from ${mappedSlots.length} to ${filteredSlots.length} slots for existing patient (Follow-Up + ANY + Telehealth types)`);
      
    } else if (patientType === 'new') {
      // For new patients: New patient types + ANY types
      const newPatientTypes = presentTypeNames.filter(name => 
        name && (name.toLowerCase().includes('new') || name.toLowerCase().includes('any'))
      );
      
      console.log("Found New Patient type names:", newPatientTypes);
      
      // Filter to only include New Patient type slots
      filteredSlots = mappedSlots.filter(slot => 
        newPatientTypes.includes(slot.patientappointmenttypename)
      );
      
      console.log(`Filtered from ${mappedSlots.length} to ${filteredSlots.length} slots for new patient (New Patient + ANY types)`);
    } else {
      // Fallback: if patient type is neither 'new' nor 'existing', return all mapped slots
      filteredSlots = mappedSlots;
    }
    
    // Sort the filtered slots by date and time
    filteredSlots.sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.starttime}`);
      const dateB = new Date(`${b.date} ${b.starttime}`);
      return dateA - dateB;
    });
    
    console.log("Final filtered and sorted slots with appointment type mapping:", 
      filteredSlots.slice(0, 5).map(s => ({
        type: s.patientappointmenttypename,
        appointmentTypeId: s.appointmenttypeid,
        duration: s.duration,
        date: s.date,
        time: s.starttime,
        originalType: s.originalAppointmentType
      }))
    );
    
    return filteredSlots;
  };

  // Helper function to filter slots based on appointment duration to prevent collisions
  const filterSlotsByDuration = (slots, duration) => {
    if (!slots.length) return [];
    
    // Sort slots by date and time
    const sortedSlots = [...slots].sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.starttime}`);
      const dateB = new Date(`${b.date} ${b.starttime}`);
      return dateA - dateB;
    });
    
    const filteredSlots = [];
    const blockedSlotIndices = new Set();
    
    // First pass: identify slots that should be blocked
    for (let i = 0; i < sortedSlots.length; i++) {
      if (blockedSlotIndices.has(i)) continue; // Skip already blocked slots
      
      const currentSlot = sortedSlots[i];
      const currentStartTime = new Date(`${currentSlot.date} ${currentSlot.starttime}`);
      const currentEndTime = new Date(currentStartTime.getTime() + duration * 60000); // Convert minutes to milliseconds
      
      // Look ahead to find slots that would be blocked by this slot
      for (let j = i + 1; j < sortedSlots.length; j++) {
        const nextSlot = sortedSlots[j];
        const nextStartTime = new Date(`${nextSlot.date} ${nextSlot.starttime}`);
        
        // If the current appointment would extend beyond the start of the next slot,
        // block the next slot
        if (currentEndTime > nextStartTime) {
          console.log(`Collision detected: ${currentSlot.starttime} (ends at ${currentEndTime.toLocaleTimeString()}) collides with ${nextSlot.starttime}`);
          blockedSlotIndices.add(j); // Block the later slot
        } else {
          // No more collisions possible with sorted slots
          break;
        }
      }
    }
    
    // Second pass: add all non-blocked slots to the result
    for (let i = 0; i < sortedSlots.length; i++) {
      if (!blockedSlotIndices.has(i)) {
        filteredSlots.push(sortedSlots[i]);
      }
    }
    
    console.log(`Filtered from ${slots.length} to ${filteredSlots.length} slots based on appointment duration`);
    return filteredSlots;
  };

  const handleClose = () => {
    setOpen(false);
  };

  const clearHandler = () => {
    setSelectedProvider("");
    setSelectedType("");
    setSelectedDepartment("");
    setSelectedReason("");
    setAppointmentReasons([]);
    setAvailableSlots([]);
    setProvider(null);
    setSelectedLocation("");
  };

  // Function to handle booking type change
  const handleBookingTypeChange = (e) => {
    const newBookingType = e.target.value;
    console.log('Booking type changed to:', newBookingType);
    setBookingType(newBookingType);
    
    // Reset selections when changing booking type
    setSelectedType('');
    setSelectedReason('');
    setAppointmentReasons([]); // Clear appointment reasons
    setSelectedProvider('');
    setSelectedLocation('');
    
    // Clear available slots
    setAvailableSlots([]);
    
    // Reset department only if we're changing between IV and regular
    if ((newBookingType === 'iv' && selectedDepartment) || 
        (newBookingType === 'appointment' && selectedDepartment)) {
      // Reload providers with filtering based on new booking type
      loadProviders(selectedDepartment);
    }
  };

  // Function to handle patient type change (new/existing)
  const handlePatientTypeChange = (e) => {
    const newPatientType = e.target.value;
    console.log(`🔄 Patient type changed from ${patientType} to ${newPatientType}`);
    
    // Reset available slots whenever patient type changes
    setAvailableSlots([]);
    console.log('Cleared available slots array');
    
    // Reset selected appointment type and reason
    setSelectedType("");
    setSelectedReason("");
    setAppointmentReasons([]);
    
    setPatientType(newPatientType);
    setIsNewPatient(newPatientType === 'new'); // Update zustand state for compatibility
    
    // CRITICAL FIX: Reload appointment types for the new patient type
    if (selectedDepartment && selectedProvider) {
      console.log(`🔄 Reloading appointment types for ${newPatientType} patient`);
      loadAppointmentTypes(selectedDepartment, selectedProvider, newPatientType);
      loadAppointmentReasons(selectedDepartment, selectedProvider, newPatientType);
    }
    
    // Clear patient selection for new patients
    if (newPatientType === 'new') {
      setPatient(null);
      setSelectedPatient(null);
      setPatientSearchResults([]);
    }
    
    // Reload appointment reasons if provider is selected - pass the new patient type directly
    // But only for regular appointments, not IV services
    if (selectedDepartment && selectedProvider && bookingType === 'appointment') {
      loadAppointmentReasons(selectedDepartment, selectedProvider, newPatientType);
    }
  };
  
  // Effect to reload providers when booking type changes
  useEffect(() => {
    console.log('Booking type effect triggered:', bookingType);
    if (selectedDepartment) {
      console.log('Reloading providers for department:', selectedDepartment);
      loadProviders(selectedDepartment);
    }
  }, [bookingType]);
  

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className="bg-[#F4F0E6]"
      maxWidth="md"
      fullWidth
      fullScreen={window.innerWidth <= 768} // Full screen on mobile
      PaperProps={{
        style: {
          borderRadius: window.innerWidth <= 768 ? 0 : 10, // Remove border radius on mobile
          margin: window.innerWidth <= 768 ? 0 : 32, // Remove margin on mobile
          maxHeight: window.innerWidth <= 768 ? '100vh' : 'calc(100vh - 64px)', // Full height on mobile
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography variant="h4" className="!font-semibold text-[#383352] uppercase !font-[Afacad]" component="div">
          Book Appointment
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {screen === 1 && (
        <DialogContent sx={{ 
          p: { xs: 2, sm: 3 }, 
          minHeight: { xs: "auto", sm: "450px" }, 
          display: "flex", 
          flexDirection: "column" 
        }}>
          <Box mb={3}>
            <Typography variant="subtitle1" className="text-[#383352] font-semibold mb-2">Select Booking Type</Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiButton-root': {
                flex: { xs: 1, sm: 'none' }
              }
            }}>
              <Button
                variant={bookingType === 'appointment' ? 'contained' : 'outlined'}
                onClick={() => handleBookingTypeChange({ target: { value: 'appointment' } })}
                sx={{
                  minWidth: { xs: 'auto', sm: '160px' },
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: bookingType === 'appointment' ? '#383352' : 'transparent',
                  borderColor: '#383352',
                  color: bookingType === 'appointment' ? 'white' : '#383352',
                  '&:hover': {
                    bgcolor: bookingType === 'appointment' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                    borderColor: '#383352'
                  }
                }}
                startIcon={<CalendarMonth />}
              >
                Book Appointment
              </Button>
              <Button
                variant={bookingType === 'iv' ? 'contained' : 'outlined'}
                onClick={() => handleBookingTypeChange({ target: { value: 'iv' } })}
                startIcon={<MedicalServices />}
                sx={{
                  minWidth: { xs: 'auto', sm: '160px' },
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: bookingType === 'iv' ? '#383352' : 'transparent',
                  borderColor: '#383352',
                  color: bookingType === 'iv' ? 'white' : '#383352',
                  '&:hover': {
                    bgcolor: bookingType === 'iv' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                    borderColor: '#383352'
                  }
                }}
              >
                Book IV Service
              </Button>
            </Box>
          </Box>
          
          <Box mb={3}>
            <Typography variant="subtitle1" className="text-[#383352] font-semibold mb-2">Patient Type</Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              '& .MuiButton-root': {
                flex: { xs: 1, sm: 'none' }
              }
            }}>
              <Button
                variant={patientType === 'new' ? 'contained' : 'outlined'}
                onClick={() => handlePatientTypeChange({ target: { value: 'new' } })}
                sx={{
                  minWidth: { xs: 'auto', sm: '160px' },
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: patientType === 'new' ? '#383352' : 'transparent',
                  borderColor: '#383352',
                  color: patientType === 'new' ? 'white' : '#383352',
                  '&:hover': {
                    bgcolor: patientType === 'new' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                    borderColor: '#383352'
                  }
                }}
              >
                New Patient
              </Button>
              <Button
                variant={patientType === 'existing' ? 'contained' : 'outlined'}
                onClick={() => handlePatientTypeChange({ target: { value: 'existing' } })}
                sx={{
                  minWidth: { xs: 'auto', sm: '160px' },
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: patientType === 'existing' ? '#383352' : 'transparent',
                  borderColor: '#383352',
                  color: patientType === 'existing' ? 'white' : '#383352',
                  '&:hover': {
                    bgcolor: patientType === 'existing' ? '#4a4568' : 'rgba(56, 51, 82, 0.04)',
                    borderColor: '#383352'
                  }
                }}
              >
                Existing Patient
              </Button>
            </Box>
          </Box>

          <Grid container spacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} sm={6} md={3.5}>
              <h6 className="text-[#383352] text-lg font-semibold">Location</h6>
              <TextField 
                select 
                fullWidth 
                label={selectedLocation ? '' : 'Location'} 
                value={selectedLocation} 
                onChange={handleLocationChange} 
                variant="outlined" 
                disabled={loading} 
                sx={{ bgcolor: "#F4F0E6" }} 
                size="small"
                InputLabelProps={{
                  shrink: false,
                }}
              >
                <MenuItem value="">Select location</MenuItem>
                {Object.keys(departmentsByLocation).map((locationName) => (
                  <MenuItem key={locationName} value={locationName}>
                    {locationName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3.5}>
              <h6 className="text-[#383352] text-lg font-semibold">Provider</h6>
              <TextField 
                select 
                fullWidth 
                label={selectedProvider ? '' : 'Provider'} 
                value={selectedProvider} 
                onChange={handleProviderChange} 
                variant="outlined" 
                disabled={!selectedDepartment || loading || (bookingType === 'iv')} 
                sx={{ bgcolor: "#F4F0E6" }} 
                size="small"
                InputLabelProps={{
                  shrink: false,
                }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 280, // Limit dropdown height
                        width: 320, // Set a reasonable width
                        marginTop: 4,
                      },
                    },
                    MenuListProps: {
                      style: {
                        padding: '4px 0',
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">Select provider</MenuItem>
                {/* Search filter inside dropdown */}
                <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                  <TextField
                    size="small"
                    placeholder="Search providers..."
                    value={providerSearchFilter}
                    onChange={(e) => setProviderSearchFilter(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        fontSize: '0.875rem',
                        backgroundColor: 'white',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <Search sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                      ),
                      endAdornment: providerSearchFilter ? (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProviderSearchFilter('');
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <Close sx={{ fontSize: 14 }} />
                        </IconButton>
                      ) : null,
                    }}
                  />
                </Box>
                {providers
                  .filter(provider => {
                    if (!providerSearchFilter) return true;
                    const displayName = provider.formattedDisplayName || provider.displayname || `${provider.firstname} ${provider.lastname}`;
                    return displayName.toLowerCase().includes(providerSearchFilter.toLowerCase());
                  })
                  .length === 0 && providerSearchFilter ? (
                  <MenuItem disabled sx={{ py: 2, px: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                    No providers found matching "{providerSearchFilter}"
                  </MenuItem>
                ) : (
                  providers
                    .filter(provider => {
                      if (!providerSearchFilter) return true;
                      const displayName = provider.formattedDisplayName || provider.displayname || `${provider.firstname} ${provider.lastname}`;
                      return displayName.toLowerCase().includes(providerSearchFilter.toLowerCase());
                    })
                    .map((provider) => (
                    <MenuItem 
                      key={provider.providerid} 
                      value={provider.providerid}
                      sx={{
                        py: 1.5,
                        px: 2,
                        fontSize: '0.875rem',
                        '&:hover': {
                          backgroundColor: 'rgba(56, 51, 82, 0.08)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(56, 51, 82, 0.12)',
                          fontWeight: 500,
                        },
                      }}
                    >
                      {provider.formattedDisplayName || provider.displayname || 'Dr. ' + provider.firstname + " " + provider.lastname}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3.5}>
              <h6 className="text-[#383352] text-lg font-semibold">Reason for visit</h6>
              <TextField 
                select 
                fullWidth 
                label={selectedReason ? '' : 'Reason for visit'} 
                value={selectedReason} 
                onChange={handleReasonChange} 
                variant="outlined" 
                disabled={!selectedProvider || loading} 
                sx={{ bgcolor: "#F4F0E6" }} 
                size="small"
                InputLabelProps={{
                  shrink: false,
                }}
              >
                <MenuItem value="">Select reason</MenuItem>
                
                {/* Display appointment reasons based on patient type */}
                {appointmentReasons.map((reason) => (
                  <MenuItem key={reason.reasonid} value={reason.reasonid}>
                    {reason.reason}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={12} md={1.5} sx={{ 
              alignSelf: { xs: "stretch", md: "end" },
              display: "flex",
              gap: 1,
              justifyContent: { xs: "center", md: "flex-start" },
              mt: { xs: 2, sm: 1, md: 0 }
            }}>
              <Button 
                onClick={clearHandler} 
                variant="contained" 
                sx={{ 
                  padding: "0", 
                  minWidth: { xs: "120px", md: "30px" }, 
                  height: "40px", 
                  width: { xs: "120px", md: "40px" },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 0 }
                }}
              >
                <Close />
                <Box component="span" sx={{ display: { xs: "inline", md: "none" } }}>Clear</Box>
              </Button>
              <Button 
                onClick={handleFindAvailableTimes} 
                disabled={!selectedDepartment || !selectedProvider || !selectedReason || loading} 
                variant="contained" 
                sx={{ 
                  padding: "0", 
                  minWidth: { xs: "120px", md: "30px" }, 
                  height: "40px", 
                  width: { xs: "120px", md: "40px" },
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 1, md: 0 }
                }}
              >
                {loading ? <CircularProgress size={20} /> : <Search />}
                <Box component="span" sx={{ display: { xs: loading ? "none" : "inline", md: "none" } }}>Search</Box>
              </Button>
            </Grid>
          </Grid>

          {/* {error && (
          <Box mt={2} p={2} bgcolor="#ffebee" borderRadius={1}>
            <Typography color="error">{error}</Typography>
          </Box>
        )} */}

          {(!selectedDepartment || !selectedProvider || !selectedReason) ? (
            <Box textAlign="center" mt={4} p={3} borderColor="divider" className="flex-grow">
              <Grid container direction={"column"} spacing={4} justifyContent="center" mt={"inherit"}>
                <div className="border-2 border-[#383352] inline-block max-w-min p-3 rounded-full mx-auto mb-3">
                  <CalendarMonth sx={{ fontSize: 40, color: "#383352" }} />
                </div>
                <p>
                  {!selectedDepartment || !selectedProvider || !selectedReason ? 
                    <>Please make selections from the section <br />above in order to view available times</> :
                    <>Click the search button to find available appointment times</>
                  }
                </p>
              </Grid>
            </Box>
          ) : (
            provider && hasSearched ? (
              <AppointmentBooking loading={loading} setScreen={setScreen} availableSlot={availableSlots} provider={provider} />
            ) : (
              <Box textAlign="center" mt={4} p={3} borderColor="divider" className="flex-grow">
                <Grid container direction={"column"} spacing={4} justifyContent="center" mt={"inherit"}>
                  <div className="border-2 border-[#383352] inline-block max-w-min p-3 rounded-full mx-auto mb-3">
                    <CalendarMonth sx={{ fontSize: 40, color: "#383352" }} />
                  </div>
                  <p>Please click search to view available times</p>
                </Grid>
              </Box>
            )
          )}
        </DialogContent>
      )}
      {screen === 2 && (
        <DialogContent sx={{ 
          p: { xs: 2, sm: 3 }, 
          minHeight: { xs: "auto", sm: "450px" }, 
          maxHeight: 'max-content', 
          display: "flex", 
          flexDirection: "column" 
        }}>
          <AppointmentUserForm setScreen={setScreen} />
        </DialogContent>
      )}
      {screen === 3 && (
        <DialogContent sx={{ 
          p: { xs: 2, sm: 3 }, 
          minHeight: { xs: "auto", sm: "450px" }, 
          maxHeight: 'max-content', 
          display: "flex", 
          flexDirection: "column" 
        }}>
          <AppointmentInsurance setScreen={setScreen} />
        </DialogContent>
      )}
      {screen === 4 && (
        <DialogContent sx={{ 
          p: { xs: 2, sm: 3 }, 
          minHeight: { xs: "auto", sm: "450px" }, 
          maxHeight: 'max-content', 
          display: "flex", 
          flexDirection: "column" 
        }}>
          <AppointmentConfirm />
        </DialogContent>
      )}
       {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </Dialog>

  );
};

export default AppointmentScheduler;
